import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import {
  Layers, FileText, Download, Trash2, Loader2, ChevronDown, X, Square,
  ClipboardCheck, Sparkles,
} from 'lucide-react';
import ComplianceChecklistModal from './ComplianceChecklistModal';
import EmailFinalPackageModal from './EmailFinalPackageModal';
import FolderReviewStatus from './FolderReviewStatus';
import { Mail } from 'lucide-react';
import { openAuthed } from '../lib/authSession';

const PACKAGE_LABELS = {
  application: 'Application',
  disclosure: 'Disclosure',
  other: 'Other Documents',
  compliance_checklist: 'Compliance Checklist',
  final: 'Final Compliance Package',
};
const FINAL_ORDER = ['final', 'compliance_checklist', 'application', 'disclosure', 'other'];

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * MergeFilesPanel — drop-in panel showing:
 *   - "Final Compliance Packages" list (Application / Disclosure / Other)
 *   - When `selectedFileIds.length > 0`, shows a merge-into picker that
 *     creates a new merged PDF from the selected files.
 *
 * Props:
 *   apiUrl              - REACT_APP_BACKEND_URL
 *   folderId            - client folder id
 *   selectedFileIds     - ordered array of file ids the user has ticked
 *   onClearSelection    - clear ticked checkboxes after merge
 *   refreshKey          - bump to force-refresh the packages list
 */
export const MergeFilesPanel = ({ apiUrl, folderId, folder, agentName, selectedFileIds, onClearSelection, refreshKey }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(false);
  const [autoMerging, setAutoMerging] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [showChecklistModal, setShowChecklistModal] = useState(false);

  const fetchPackages = useCallback(async () => {
    if (!folderId) { setPackages([]); return; }
    setLoading(true);
    try {
      const r = await fetch(`${apiUrl}/api/client-files/${folderId}/packages`);
      if (r.ok) setPackages(await r.json());
    } catch (e) {
      console.error('Failed to fetch packages:', e);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, folderId]);

  useEffect(() => { fetchPackages(); }, [fetchPackages, refreshKey]);

  const [building, setBuilding] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const buildFinal = async (silent = false) => {
    setBuilding(true);
    try {
      const r = await fetch(`${apiUrl}/api/client-files/${folderId}/final-package`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { if (!silent) alert(d.detail || 'Could not build the final package.'); return false; }
      await fetchPackages();
      if (!silent) {
        const c = d.compression || {};
        const mb = (b) => `${(b / 1024 / 1024).toFixed(1)} MB`;
        const sizeNote = c.compressed ? ` Compressed from ${mb(c.original_size)} to ${mb(d.package?.file_size || 0)}${c.under_target ? '' : ' (could not get under 15 MB)'}.` : ` Size: ${mb(d.package?.file_size || 0)}.`;
        alert(`Final Compliance Package built (${d.page_count} pages): ${(d.included || []).map(t => PACKAGE_LABELS[t]).join(' → ')}.${sizeNote} It is now listed under Deal Compliance Review as "For Review".`);
      }
      return true;
    } finally {
      setBuilding(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this merged package? You can always re-merge from the source files.')) return;
    try {
      const r = await fetch(`${apiUrl}/api/client-files/file/${fileId}`, { method: 'DELETE' });
      if (r.ok) fetchPackages();
    } catch (e) {
      console.error('Delete failed:', e);
    }
  };

  const has = (t) => packages.some(p => p.package_type === t);
  const readyForFinal = has('compliance_checklist') && (has('application') || has('disclosure') || has('other'));
  const sortedPackages = [...packages].sort((x, y) => FINAL_ORDER.indexOf(x.package_type) - FINAL_ORDER.indexOf(y.package_type));

  return (
    <div className="mt-4 border rounded-lg bg-white" data-testid="merge-files-panel">
      {/* Merge action bar */}
      <div className="p-3 border-b bg-gradient-to-r from-emerald-50 to-teal-50 rounded-t-lg flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-gray-700">
          <Layers className="h-4 w-4 text-emerald-700" />
          <span className="text-sm font-semibold">Final Compliance Packages</span>
          <span className="text-xs text-gray-500">({packages.length})</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {folder && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowChecklistModal(true)}
              className="border-[#0B3D7A] text-[#0B3D7A] hover:bg-[#0B3D7A] hover:text-white"
              data-testid="open-compliance-checklist-btn"
            >
              <ClipboardCheck className="h-4 w-4 mr-1" />
              {has('compliance_checklist') ? 'Redo Compliance Checklist' : 'Compliance Checklist'}
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => buildFinal(false)}
            disabled={building || !readyForFinal}
            className="bg-[#0077B6] hover:bg-[#005A8C]"
            title={readyForFinal ? 'Merge Checklist → Application → Disclosure → Other into one PDF' : 'Merge the 3 packages and complete the Compliance Checklist first'}
            data-testid="build-final-package-btn"
          >
            {building ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Layers className="h-4 w-4 mr-1" />}
            {has('final') ? 'Rebuild Final Package' : 'Build Final Package'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowEmailModal(true)}
            disabled={!has('final')}
            className="border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white"
            title={has('final') ? 'Email the Final Compliance Package to the compliance officer' : 'Build the Final Package first'}
            data-testid="email-final-package-btn"
          >
            <Mail className="h-4 w-4 mr-1" />Email to Compliance
          </Button>
        </div>
      </div>

      {/* Packages list */}
      <div className="p-3">
        {loading ? (
          <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-gray-400" /></div>
        ) : packages.length === 0 ? (
          <p className="text-xs text-gray-500 text-center py-4">No compliance packages yet.</p>
        ) : (
          <div className="space-y-2">
            {sortedPackages.map(pkg => (
              <div
                key={pkg.id}
                className="flex items-center justify-between p-2.5 bg-emerald-50/40 border border-emerald-100 rounded-md"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-gray-800 text-sm truncate">{pkg.filename}</p>
                      <span className={`text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded text-white ${pkg.package_type === 'final' ? 'bg-[#0B3D7A]' : 'bg-emerald-600'}`}>
                        {PACKAGE_LABELS[pkg.package_type] || pkg.package_type}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {pkg.page_count} page(s) • {formatFileSize(pkg.file_size)} • {new Date(pkg.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button variant="outline" size="sm" asChild>
                    <a
                      href={`${apiUrl}/api/client-files/download/${pkg.id}`}
                      onClick={openAuthed}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`download-package-${pkg.id}`}
                    >
                      <Download className="h-4 w-4 mr-1" />Download
                    </a>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(pkg.id)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-package-${pkg.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        {folderId && <FolderReviewStatus apiUrl={apiUrl} folderId={folderId} refreshKey={`${refreshKey}-${packages.length}-${packages.find(p => p.package_type === 'final')?.id || ''}`} />}
      </div>
      {showEmailModal && (
        <EmailFinalPackageModal
          apiUrl={apiUrl}
          folderId={folderId}
          finalPkg={packages.find(p => p.package_type === 'final')}
          onClose={() => setShowEmailModal(false)}
        />
      )}

      {showChecklistModal && folder && (
        <ComplianceChecklistModal
          apiUrl={apiUrl}
          folder={folder}
          agentName={agentName}
          onClose={() => setShowChecklistModal(false)}
          onCreated={async () => { await fetchPackages(); const ok = await buildFinal(true); if (!ok) alert('Checklist saved. Merge the Application / Disclosure / Other packages, then click "Build Final Package".'); else alert('Checklist saved and the Final Compliance Package was assembled: Checklist → Application → Disclosure → Other Documents.'); }}
        />
      )}
    </div>
  );
};

export default MergeFilesPanel;
